import React, { JSX, useEffect, useState } from 'react';
import Icons from 'components/atoms/Icons';
import { myChart } from '../shared/MyChartConfig';
import { useTracking } from 'lib/tracking';

type LocationApptQuestionsProps = {
  deptId: string;
  phone: string;
};

type Answers = {
  [key: string]: string;
};

const LocationApptQuestions = (
  props: LocationApptQuestionsProps
): JSX.Element => {
  const { deptId, phone } = props;
  const [isFinished, setIsFinished] = useState<boolean | null>(false);
  const { linksource } = useTracking();

  useEffect(() => {
    const el = document && document.getElementById('appt-qs');
    const disclosureButton = el?.querySelector('.header');
    const questions = el?.querySelector('.questions');
    const questionHistory = el?.querySelectorAll('.question-history');
    const questionContent = el?.querySelectorAll('.question-content');
    const answerButtons = el?.querySelectorAll('button');
    const iframeElement = el?.querySelector('iframe');
    const profileScheduling = el?.querySelector('.profile-scheduling');
    const myChartIframe: HTMLIFrameElement | null | undefined =
      el?.querySelector('iframe');
    const stopAnswer: Answers = {
      '1': 'no',
      '2': 'no',
      '3': 'yes',
      '4': 'yes',
      '5': 'n/a',
    };
    const nextQuestion: Answers = {
      '1': '2',
      '2': '3',
      '3': '4',
      '4': '5',
      '5': 'valid-questions',
    };

    const startOver = () => {
      setIsFinished(false);
      questionHistory?.forEach((item: Element) => {
        item.classList.contains('active') && item.classList.remove('active');
      });
      questionContent?.forEach((item: Element) => {
        item.classList.contains('active') && item.classList.remove('active');
        const startSection = item.querySelector('.start');
        startSection && startSection.classList.add('active');
        const stopSection = item.querySelector('.stop');
        stopSection && stopSection.classList.remove('active');
      });
      const question1 = el?.querySelector('[data-question="1"]');
      question1 && question1.classList.add('active');
    };

    const addQuestionHistory = (questionNumber: string, answer: string) => {
      const clearLastElement = el?.querySelectorAll('.question-history');
      clearLastElement?.forEach((lastElement: Element) => {
        lastElement.classList.remove('last');
      });

      const historyQuestion = el?.querySelector(
        `[data-question-history-number="${questionNumber}"]`
      );
      const progress = parseInt(questionNumber) * 20;
      historyQuestion &&
        (historyQuestion.classList.add('active', 'last'),
        historyQuestion.setAttribute(
          'style',
          `--question-history-progress: ${progress}%`
        ));

      const answers = historyQuestion?.querySelectorAll('span');
      answers?.forEach((answer: HTMLSpanElement) => {
        answer.classList.remove('selected');
      });
      const historyAnswer = historyQuestion?.querySelector(
        `[data-answer="${answer}"]`
      );
      historyAnswer && historyAnswer.classList.add('selected');
    };

    const stopQuestion = (questionNumber: string, answer: string) => {
      addQuestionHistory(questionNumber, answer);
      const question = el?.querySelector(`[data-question="${questionNumber}"]`);

      const startSection = question?.querySelector('.start');
      startSection && startSection.classList.remove('active');
      const stopSection = question?.querySelector('.stop');
      stopSection && stopSection.classList.add('active');
    };

    const switchToSchedule = () => {
      profileScheduling && profileScheduling.classList.add('active');
      el?.classList.remove('expanded');

      return true;
    };

    const askNextQuestion = (questionNumber: string, answer: string) => {
      setIsFinished(
        answer === 'showAvailableOpenings' ? switchToSchedule() : null
      );

      if (!isFinished) {
        addQuestionHistory(questionNumber, answer);
        questionContent?.forEach((item: Element) => {
          item.classList.contains('active') && item.classList.remove('active');
        });
        const nextContent = questions?.querySelector(
          `[data-question="${nextQuestion[questionNumber]}"]`
        );
        nextContent && nextContent.classList.add('active');
      }
    };

    const questionSequence = (data: DOMStringMap) => {
      const iframeSrc = `${myChart.openSchedulingBaseIframeSrc}?dept=${deptId}&vt=${data.mychartVt}&apikey=${myChart.apiKey}&linksource=${linksource}`;
      data.mychartVt && iframeElement?.setAttribute('src', iframeSrc);
      const shouldStop =
        stopAnswer[data.questionNumber as string] === data.answer;
      shouldStop
        ? stopQuestion(data.questionNumber as string, data.answer as string)
        : askNextQuestion(data.questionNumber as string, data.answer as string);
    };

    const questionAnswer = (data: DOMStringMap) => {
      data.answer === 'startOver' ? startOver() : questionSequence(data);
    };

    const toggleQuestions = () => {
      if (el?.classList.contains('expanded')) {
        el.classList.remove('expanded');

        isFinished && profileScheduling?.classList.add('active');
      } else {
        el?.classList.add('expanded');

        profileScheduling?.classList.remove('active');
      }
    };

    disclosureButton?.addEventListener('click', () => {
      toggleQuestions();
    });

    answerButtons?.forEach(item => {
      item.addEventListener('click', e => {
        e.preventDefault();
        questionAnswer(item.dataset);
      });
    });

    const frameSrc = myChartIframe && myChartIframe.getAttribute('src');

    !/(osfhealthcare.org)/.test(location.hostname) &&
      frameSrc &&
      myChartIframe &&
      myChartIframe.setAttribute(
        'src',
        frameSrc.replace(
          'www.osfmychart.org',
          'pmc-pia-apsd.intranet.osfnet.org'
        )
      );
  });

  return (
    <div
      className="location-appt-questions expanded"
      data-module="AppointmentQuestionsFactory"
      id="appt-qs"
      data-mychart-dept={deptId}
    >
      <div className="questions">
        <div className="question-history" data-question-history-number="1">
          Will this visit be billed to your personal insurance?
          <div className="answers">
            <span data-answer="no">No</span>
            <span data-answer="yes">Yes</span>
          </div>
        </div>
        <div className="question-history" data-question-history-number="2">
          Is this appointment for a screening mammogram (either your
          first/baseline or an annual exam)?{' '}
          <div className="answers">
            <span data-answer="no">No</span>
            <span data-answer="yes">Yes</span>
          </div>
        </div>
        <div className="question-history" data-question-history-number="3">
          Has a doctor recommended a specific area to follow or investigate, or
          do you have any new breast health symptoms (such as discharge, lumps,
          or specific breast pain)?{' '}
          <div className="answers">
            <span data-answer="no">No</span>
            <span data-answer="yes">Yes</span>
          </div>
        </div>
        <div className="question-history" data-question-history-number="4">
          Have you had a mammogram in the past year?{' '}
          <div className="answers">
            <span data-answer="no">No</span>
            <span data-answer="yes">Yes</span>
          </div>
        </div>
        <div className="question-history" data-question-history-number="5">
          Do you have breast implants?{' '}
          <div className="answers">
            <span data-answer="no">No</span>
            <span data-answer="yes">Yes</span>
          </div>
        </div>

        <div className="question-content active" data-question="1">
          <div className="start active">
            <div className="question">
              <h5>Please answer the following five questions:</h5>
              <p>Will this visit be billed to your personal insurance?</p>
            </div>

            <div className="action-answer">
              <button
                className="action-btn"
                data-question-number="1"
                data-answer="no"
              >
                No
              </button>
              <button
                className="action-btn"
                data-question-number="1"
                data-answer="yes"
              >
                Yes
              </button>
            </div>
          </div>
          <div className="stop">
            <div className="stop-question">
              <Icons id="information" className="icon-information" />
              <p>
                We are unable to complete your appointment request through our
                online system. As a result of your self-pay selection, we are
                required to discuss your financial options and provide you with
                an accurate estimate for services before proceeding. Scheduling
                must be completed by phone so our team can work with you to
                complete this process.
              </p>
              <p>
                Please visit our{' '}
                <a href="https://www.osfhealthcare.org/breast-health/services/mammogram#schedule">
                  Breast Health Mammogram
                </a>{' '}
                web page to see a list of OSF HealthCare locations you may call
                to further assist you in scheduling your appointment. If you
                feel you have received this message in error, please click the
                button below to start over and enter your correct insurance
                information.
              </p>
            </div>
            <div className="stop-answer">
              <button className="action-btn start-over" data-answer="startOver">
                Start Over
                <Icons id="start-over" />
              </button>
              <a className="action-btn" href={`tel:${phone}`}>
                Call
                <span className="action-btn-subtitle">{phone}</span>
                <Icons id="phone" />
              </a>
            </div>
          </div>
        </div>
        <div className="question-content" data-question="2">
          <div className="start active">
            <div className="question">
              <p>
                Is this appointment for your first, baseline or annual screening
                mammogram?
              </p>
            </div>

            <div className="action-answer">
              <button
                className="action-btn"
                data-question-number="2"
                data-answer="no"
              >
                No
              </button>
              <button
                className="action-btn"
                data-question-number="2"
                data-answer="yes"
              >
                Yes
              </button>
            </div>
          </div>
          <div className="stop">
            <div className="stop-question">
              <Icons id="information" className="icon-information" />
              <p>You might need a different appointment.</p>
              <p>Please call us. We can help.</p>
            </div>
            <div className="stop-answer">
              <button className="action-btn start-over" data-answer="startOver">
                Start Over
                <Icons id="start-over" />
              </button>
              <a className="action-btn" href={`tel:${phone}`}>
                Call
                <span className="action-btn-subtitle">{phone}</span>
                <Icons id="phone" />
              </a>
            </div>
          </div>
        </div>
        <div className="question-content" data-question="3">
          <div className="start active">
            <div className="question">
              <p>
                Has a doctor recommended a specific area to follow or
                investigate, or do you have any new breast health symptoms (such
                as discharge, lumps, or specific breast pain)?
              </p>
            </div>
            <div className="action-answer">
              <button
                className="action-btn start-over lrg-btn"
                data-answer="startOver"
              >
                Start Over
                <Icons id="start-over" />
              </button>
              <button
                className="action-btn"
                data-question-number="3"
                data-answer="no"
              >
                No
              </button>
              <button
                className="action-btn"
                data-question-number="3"
                data-answer="yes"
              >
                Yes
              </button>
            </div>
          </div>
          <div className="stop">
            <div className="stop-question">
              <Icons id="information" className="icon-information" />
              <p>
                Symptomatic women and women with previously detected abnormality
                for whom short interval follow-up or further evaluation has been
                recommended, are not candidates for screening mammography.
              </p>
              <p>Please call us, and we can help.</p>
            </div>
            <div className="stop-answer">
              <button className="action-btn start-over" data-answer="startOver">
                Start Over
                <Icons id="start-over" />
              </button>
              <a className="action-btn" href={`tel:${phone}`}>
                Call
                <span className="action-btn-subtitle">{phone}</span>
                <Icons id="phone" />
              </a>
            </div>
          </div>
        </div>
        <div className="question-content" data-question="4">
          <div className="start active">
            <div className="question">
              <p>Have you had a mammogram in the past year?</p>
            </div>

            <div className="action-answer">
              <button
                className="action-btn start-over lrg-btn"
                data-answer="startOver"
              >
                Start Over
                <Icons id="start-over" />
              </button>
              <button
                className="action-btn"
                data-question-number="4"
                data-answer="no"
              >
                No
              </button>
              <button
                className="action-btn"
                data-question-number="4"
                data-answer="yes"
              >
                Yes
              </button>
            </div>
          </div>
          <div className="stop">
            <div className="stop-question">
              <Icons id="information" className="icon-information" />
              <p>
                Most insurance plans only cover one screening mammogram per
                year. Please be sure to schedule your next mammogram at least
                one year out from your last mammogram.
              </p>
            </div>
            <div className="stop-answer">
              <button className="action-btn start-over" data-answer="startOver">
                Start Over
                <Icons id="start-over" />
              </button>
              <a className="action-btn" href={`tel:${phone}`}>
                Call
                <span className="action-btn-subtitle">{phone}</span>
                <Icons id="phone" />
              </a>
            </div>
          </div>
        </div>
        <div className="question-content" data-question="5">
          <div className="start active">
            <div className="question">
              <p>Do you have breast implants?</p>
            </div>

            <div className="action-answer">
              <button
                className="action-btn start-over lrg-btn"
                data-answer="startOver"
              >
                Start Over
                <Icons id="start-over" />
              </button>
              <button
                className="action-btn"
                data-question-number="5"
                data-mychart-vt="154"
                data-answer="no"
              >
                No
              </button>
              <button
                className="action-btn"
                data-question-number="5"
                data-mychart-vt="180"
                data-answer="yes"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
        <div className="question-content" data-question="valid-questions">
          <div className="start active">
            <div className="question">
              <p>Thank you.</p>
              <p>
                You meet all the criteria for the procedure. Let's review
                available openings.
              </p>
            </div>

            <div className="action-answer">
              <button className="action-btn start-over" data-answer="startOver">
                Start Over
                <Icons id="start-over" />
              </button>
              <button
                className="action-btn"
                data-answer="showAvailableOpenings"
              >
                Show Available Openings
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="profile-scheduling"
        data-module="ProfileSchedulingFactory"
      >
        <div className="schedule-appointment" data-module="ExpandableFactory">
          <div className="patient-content" data-module="ExpandableFactory">
            <div className="schedule-widget active" data-section="new">
              <div className="inner-content modal-inner">
                <iframe
                  src=""
                  frameBorder="0"
                  allowFullScreen
                  title="New Patient Appointment Form"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationApptQuestions;
